import { useMemo } from "react";
import { Checklist, ChecklistItem, type ChecklistItemStatus } from "@components/shared/checklist";
import { Deploy, type Core } from "@shared";
import {
  BugAntIcon,
  MagnifyingGlassIcon,
  ChatBubbleBottomCenterTextIcon,
  ChartBarIcon,
  RocketLaunchIcon,
  MegaphoneIcon,
  BoltIcon,
  CreditCardIcon,
  SignalIcon,
} from "@heroicons/react/24/solid";
import type { ComponentType, SVGProps } from "react";
import type { DeployTaskRecord } from "@api/deploys.hooks";

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

/** Google "G" icon as inline SVG component */
function GoogleIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" {...props}>
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

const PHASE_ICON_MAP: Record<Deploy.PhaseName, IconComponent> = {
  ConnectingGoogle: GoogleIcon as IconComponent,
  VerifyingGoogle: SignalIcon,
  CheckingBilling: CreditCardIcon,
  CheckingForBugs: MagnifyingGlassIcon,
  FixingBugs: BugAntIcon,
  OptimizingSEO: ChatBubbleBottomCenterTextIcon,
  OptimizingPageForLLMs: ChatBubbleBottomCenterTextIcon,
  AddingAnalytics: ChartBarIcon,
  DeployingWebsite: RocketLaunchIcon,
  DeployingCampaign: MegaphoneIcon,
  EnablingCampaign: BoltIcon,
};

const PHASE_LABELS: Record<Deploy.PhaseName, string> = {
  ConnectingGoogle: "Signing into Google",
  VerifyingGoogle: "Verifying Google Account",
  CheckingBilling: "Checking payment status",
  CheckingForBugs: "Checking for bugs",
  FixingBugs: "Squashing bugs",
  OptimizingSEO: "Optimizing SEO",
  OptimizingPageForLLMs: "Optimizing for LLMs",
  AddingAnalytics: "Connecting Analytics",
  DeployingWebsite: "Launching Website",
  DeployingCampaign: "Syncing Campaign",
  EnablingCampaign: "Enabling Campaign",
};

function toChecklistStatus(phaseStatus: Core.Status | string): ChecklistItemStatus {
  switch (phaseStatus) {
    case "completed":
    case "passed":
      return "completed";
    case "failed":
      return "failed";
    case "running":
      return "in_progress";
    case "skipped":
      return "pending";
    default:
      return "pending";
  }
}

const CAMPAIGN_PHASE_NAMES: string[] = [
  "ConnectingGoogle",
  "VerifyingGoogle",
  "CheckingBilling",
  "DeployingCampaign",
  "EnablingCampaign",
];

/** Compute phase status from Rails task records using shared logic */
function computePhaseStatusFromTasks(
  phaseName: Deploy.PhaseName,
  tasks: DeployTaskRecord[]
): Core.Status {
  const taskNames = Deploy.PhaseTaskMap[phaseName];
  const phaseTasks = tasks.filter((t) => taskNames.includes(t.name as Deploy.TaskName));
  // Map DeployTaskRecord to the shape Deploy.computePhaseStatus expects
  return Deploy.computePhaseStatus(phaseTasks as unknown as Deploy.Task[]);
}

interface DeployTaskListProps {
  tasks?: DeployTaskRecord[];
  deployStatus?: string;
  /** Override Checklist header padding (e.g. "p-0" in a compact popover). */
  headerClassName?: string;
  /** Hide the "Launching Website" title when a surrounding header already names the step. */
  hideTitle?: boolean;
}

export default function DeployTaskList({
  tasks,
  deployStatus,
  headerClassName,
  hideTitle,
}: DeployTaskListProps) {
  const { phases, hasCampaignPhases } = useMemo(() => {
    const allTasks = tasks ?? [];
    const taskNames = new Set(allTasks.map((t) => t.name));

    const computed = (Deploy.PhaseNames as readonly Deploy.PhaseName[])
      .filter((phaseName) => {
        const phaseTasks = Deploy.PhaseTaskMap[phaseName];
        const hasTask = phaseTasks.some((taskName) => taskNames.has(taskName));
        if (!hasTask) return false;

        if (phaseName === "FixingBugs") {
          if (!taskNames.has("FixingBugs")) return false;
        }
        return true;
      })
      .map((phaseName) => ({
        name: phaseName,
        status: computePhaseStatusFromTasks(phaseName, allTasks),
      }));

    return {
      phases: computed,
      hasCampaignPhases: computed.some((p) => CAMPAIGN_PHASE_NAMES.includes(p.name)),
    };
  }, [tasks]);

  const title = hasCampaignPhases ? "Launching Campaign" : "Launching Website";

  return (
    <Checklist.Root title={title} className={headerClassName} hideTitle={hideTitle}>
      {phases.length > 0 ? (
        <Checklist.Items>
          {phases.map((phase) => (
            <ChecklistItem
              key={phase.name}
              icon={PHASE_ICON_MAP[phase.name]}
              label={PHASE_LABELS[phase.name]}
              status={toChecklistStatus(phase.status)}
            />
          ))}
        </Checklist.Items>
      ) : deployStatus === "completed" ? (
        <Checklist.Items>
          <ChecklistItem icon={RocketLaunchIcon} label="Deploy complete" status="completed" />
        </Checklist.Items>
      ) : (
        <Checklist.Empty message="Preparing deployment..." />
      )}
    </Checklist.Root>
  );
}
